import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SET_UPI_DATA, useDeviceSimList, useOnBoardingRedirection, useSelectedSim, useUpiData } from 'Slices';
import { IDispatcherType, ISimDetailsObject } from 'types';
import history from 'utils/History';
import {
	fetchInitTpapSdkStatus,
	fetchUpiProfile,
	TriggerNativeApiWrapper,
	UPIActivateAccount,
} from 'utils/nativeUpiHandler';
import CardIcon from 'assets/icons/Autopay/card.svg';
import { BINNDING_STAGES } from '../constant';
import { isEmpty } from 'utils/Utils';
import { useMerchantInfo } from 'Slices/MercantInfoSlice';
import GenericLoader from 'components/organisms/GenericLoader';
import AllModals from 'containers/UPIContainer/Modals';
import AlertModal from 'containers/UPIContainer/Modals/AlertModal';
import Toasty from 'components/atoms/Toasty';
import { Loader, PageLayout } from '@bharatpe/bharat-ui';
import { customSubScribeBackButton, getAppInfo } from 'utils/nativeSupport';
import UPIActivationErrorModal from './UPIActivationErrorModal';
import { API_URL } from 'Services/CONSTANT';
import { checkSettlementAccountMatch, deviceBindingRedirectionHandle } from 'utils/deviceBindingHelper';
import { sendEvent } from 'utils/Sentry';
import { ONBOARDING_REDIRECTION_PAGE } from 'utils/CONSTANT';
import { triggerCustomerSettlementAccountApi } from 'Slices/GetCustomerSettlementAccount';
import { triggerSettlementAccountApi } from 'Slices/GetSettlementAccount';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';

interface IErrorObj {
	state?: string;
	error?: string;
	errorCode?: string;
}

const UPIActivationStatus: React.FC = () => {
	const hist: any = history.location.state;
	const selectedSim = hist?.selectedSim;
	const upiData = useUpiData();
	const sim = useSelectedSim();
	const deviceSimList = useDeviceSimList();
	const merchantInfo = useMerchantInfo();
	const [errorObj, setErrObj] = useState<IErrorObj>({});
	const [deviceBindingSuccess, setDeviceBindingSuccess] = useState(false);
	const [selectedBankAccount, setSelectedBankAccount] = useState<any>(null);
	const [showDebitCardModal, setShowDebitCardModal] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch<IDispatcherType>();
	const onBoardingRedirection = useOnBoardingRedirection();
	const attemptedSim = useSelectedSim();
	const { languageData } = useLanguageConfig();

	useEffect(() => {
		switch (onBoardingRedirection.source) {
			case ONBOARDING_REDIRECTION_PAGE.PROFILE:
				dispatch(triggerCustomerSettlementAccountApi());

				break;
			case ONBOARDING_REDIRECTION_PAGE.MANDATE:
				dispatch(triggerSettlementAccountApi());
				break;
		}
	}, [dispatch]);

	useEffect(() => {
		logAnalyticsEvent({
			trigger_point: 'Page View',
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'SMSVerification',
			Module: 'LoadingPage',
			Action: 'Viewed',
		});
	}, []);

	const statusUpdateCallback = (data: any) => {
		const { state, error, errorCode } = data;
		if (error || errorCode) {
			setErrObj(data);
			return;
		}
		if (state == BINNDING_STAGES.PERMISSIONS_STATUS) {
			return;
		}
	};

	const fetchUpiProfileDetails = async (profileId?: string) => {
		try {
			if (upiData.userActivationStatus === 'INACTIVE') {
				await TriggerNativeApiWrapper(API_URL.create_user, 'POST', { profile_id: profileId }, getAppInfo() as any);
			}
			const data = await fetchUpiProfile(profileId || '');
			setDeviceBindingSuccess(false);
			const { profileDetails } = data as any;
			const { reclaimStatus, user_banks } = profileDetails;
			setSelectedBankAccount(user_banks[0]);
			if (profileDetails) {
				dispatch(
					SET_UPI_DATA({ ...upiData, profileId: profileId, upiProfileDetails: profileDetails, upiDataFetched: true }),
				);
				if (reclaimStatus && user_banks[0]) {
					history.push({
						pathname: '/upi/reclaim-bank-list',
					});
					return;
				}
				const isMatchedSettlementAccount = await checkSettlementAccountMatch(onBoardingRedirection.source);
				if (!isMatchedSettlementAccount) {
					history.push({
						pathname: '/upi/bank-selection',
						state: { selectedSim: sim },
					});
					return;
				}
				deviceBindingRedirectionHandle();
				return;
			}
		} catch (err: any) {
			try {
				if (err?.errorCode == '404') {
					await TriggerNativeApiWrapper(API_URL.create_user, 'POST', { profile_id: profileId }, getAppInfo() as any);
					fetchUpiProfileDetails(profileId);
					return;
				}
				setDeviceBindingSuccess(false);
				setErrObj(err);
			} catch (err: any) {
				setDeviceBindingSuccess(false);
				setErrObj(err);
			}
		}
	};

	const getUpiProfileDetails = async (profileId?: string) => {
		try {
			const data = await fetchUpiProfile(profileId || '');
			const { profileDetails } = data as any;
			if (profileDetails) {
				dispatch(SET_UPI_DATA({ ...upiData, upiProfileDetails: profileDetails, upiDataFetched: true }));
			}
		} catch (err: any) {
			Toasty.error('Something went wrong!!!');
			sendEvent(err);
		}
	};

	const handleSaveDebitDetails = async () => {
		setLoading(true);
		try {
			const data = await UPIActivateAccount(selectedBankAccount);
			if (data) {
				await getUpiProfileDetails('');
			}
			setShowDebitCardModal(false);
			setSelectedBankAccount(false);
			setLoading(false);
			deviceBindingRedirectionHandle();
			setLoading(false);
		} catch (err: any) {
			setLoading(false);
			setShowDebitCardModal(false);
			setErrObj(err);
		}
	};

	const initDeviceBinding = async (forceDeviceBinding: boolean) => {
		try {
			const selectedSimData = deviceSimList.filter((item: ISimDetailsObject) => {
				return String(selectedSim) == String(item.sim_id);
			})?.[0];
			setDeviceBindingSuccess(true);
			const deviceBindingData: any = await fetchInitTpapSdkStatus(
				forceDeviceBinding,
				selectedSim?.phone,
				selectedSim || {},
				statusUpdateCallback,
			);
			if (deviceBindingData?.bindingComplete) {
				dispatch(SET_UPI_DATA({ ...upiData, initDeviceBinding: true, upiDataFetched: true }));
				dispatch(SET_UPI_DATA({ ...upiData, profileId: deviceBindingData.profileId }));
				if (deviceBindingData.profileId) {
					fetchUpiProfileDetails(deviceBindingData.profileId);
				} else {
					setDeviceBindingSuccess(false);
					history.push({
						pathname: '/upi/bank-selection',
						state: { selectedSim: sim },
					});
				}
			} else {
				setDeviceBindingSuccess(false);
				Toasty.error('Seomething went wrong');
			}
		} catch (err: any) {
			setDeviceBindingSuccess(false);
			setErrObj(err);
		}
	};

	const deviceBindingCompleteRedirection = () => {
		const { upiProfileDetails: { user_banks = [] } = {} } = upiData;
		if (isEmpty(user_banks)) {
			history.push({
				pathname: '/upi/upi-activation-status',
			});
			return;
		}
	};

	useEffect(() => {
		if (!isEmpty(attemptedSim) || upiData.userActivationStatus === 'INACTIVE') {
			initDeviceBinding(true);
		} else {
			initDeviceBinding(false);
		}
	}, [attemptedSim]);

	useEffect(() => {
		customSubScribeBackButton(() => {
			return;
		});
	}, [history]);

	return loading ? (
		<Loader loading />
	) : (
		<PageLayout>
			<>
				<AllModals />
				{deviceBindingSuccess && (
					<GenericLoader primaryText={languageData.SENDING} secondryText={languageData.VERIFICATION_SMS} />
				)}
				<AlertModal
					handleClick={handleSaveDebitDetails}
					title={languageData.SET_PIN_WITH_DEBIT_CARD}
					description={languageData.KINDLY_PROCEED_ONLY_IF_YOU_HAVE_THE_SAME_MOBILE_NUMBER_MOBILENUMBER_REGISTERED_WITH_DEBIT_CARD_KEEP_YOUR_DEBIT_CARD_WITH_YOU_TO_VARIFY_16_DIGIT_NUMBER.replace(
						'{0}',
						merchantInfo?.data?.mobile,
					)}
					buttonName={languageData.CONTINUE}
					icon={CardIcon}
					type={'info'}
					visible={showDebitCardModal}
					showCloseButton={false}
				/>
				{(!isEmpty(errorObj?.error) || !isEmpty(errorObj?.errorCode)) && (
					<UPIActivationErrorModal
						errorObject={errorObj}
						handlePrimaryButtonClick={() => {
							deviceBindingRedirectionHandle(true);
						}}
					/>
				)}
			</>
		</PageLayout>
	);
};

export default UPIActivationStatus;
